import { useEffect, useState } from "react";
import { Download, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { downloadMcaPrefill, listMcaPrefillForms } from "@/lib/api";

function currentFyLabel() {
  const d = new Date();
  const y = d.getFullYear();
  const sy = d.getMonth() >= 3 ? y : y - 1;
  return `${sy}-${String((sy + 1) % 100).padStart(2, "0")}`;
}

/**
 * MCA V3 Filing Cheat Sheet modal — pick a form + FY → download a printable
 * pre-fill PDF that mirrors every screen of the MCA V3 form.
 *
 * Available forms (today): AOC-4, MGT-7A. Listed dynamically from the
 * `/api/mca-prefill/forms` endpoint so adding more on the backend automatically
 * surfaces them in the UI.
 */
export function McaPrefillModal({ company, onClose }) {
  const [forms, setForms] = useState([]);
  const [selected, setSelected] = useState("");
  const [fy, setFy] = useState(currentFyLabel());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listMcaPrefillForms()
      .then((res) => {
        const list = res.data?.forms || [];
        setForms(list);
        if (list[0]) setSelected(list[0].key);
      })
      .catch(() => toast.error("Could not load supported forms"));
  }, []);

  const download = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await downloadMcaPrefill(company.id, selected, fy);
      toast.success(`${selected.toUpperCase()} cheat sheet downloaded`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate cheat sheet");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { if (!busy) onClose(); }}>
      <div
        className="bg-white max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="mca-prefill-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">MCA V3 · Filing Cheat Sheet</div>
            <h2 className="text-lg font-semibold mt-1">Auto-fill an MCA V3 form</h2>
          </div>
          <button onClick={onClose} data-testid="mca-prefill-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-slate-500 mb-4 leading-relaxed">
          Generates a printable PDF (and JSON) with every field of the chosen V3 form
          pre-populated from this company's master data — keep it open on a second
          monitor while filling V3. <strong>Direct API submission to MCA is not
          publicly available;</strong> you'll still upload through the MCA portal yourself.
        </p>

        <div className="space-y-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Form</span>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 mt-1 text-sm focus:outline-none focus:border-blue-700"
              data-testid="mca-prefill-form-select"
            >
              {forms.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
            {selected && forms.find((f) => f.key === selected)?.section && (
              <div className="text-[10px] text-slate-500 mt-1 italic">
                {forms.find((f) => f.key === selected).section} · Due: {forms.find((f) => f.key === selected).due}
              </div>
            )}
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Financial Year</span>
            <input
              value={fy}
              onChange={(e) => setFy(e.target.value)}
              placeholder="2025-26"
              className="w-full border border-slate-300 px-3 py-2 mt-1 text-sm focus:outline-none focus:border-blue-700"
              data-testid="mca-prefill-fy"
            />
          </label>

          <div className="bg-blue-50 border-l-2 border-l-blue-700 p-3 text-[11px] text-slate-700 leading-relaxed">
            <FileText className="w-3 h-3 inline mr-1 -mt-0.5" /> The cheat sheet covers
            all V3 screens for the chosen form. Blank values mean the data isn't on
            ROC Vault yet — add Members / Directors / Professionals / Resolutions
            before exporting for cleanest output.
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-sm">
            Cancel
          </button>
          <button
            onClick={download}
            disabled={busy || !selected}
            className="px-4 py-2 bg-blue-700 text-white text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="mca-prefill-download"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Download Cheat Sheet
          </button>
        </div>
      </div>
    </div>
  );
}
